import { useCallback, useEffect, useState } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { errMsg } from "../../lib/api";
import { StatCard, StageBadge, FuBadge, QuickActions, Timeline, fmtINR } from "./CrmShared";

const PIE = ["#10b981", "#ef4444"];
const CHART = "#7c6cf0";

export default function CrmDashboard({ refreshKey, goTab }) {
  const [s, setS] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => {
    api.get("/store-admin/crm/summary").then((r) => setS(r.data)).catch((e) => setErr(errMsg(e)));
  }, []);
  useEffect(() => { load(); }, [refreshKey, load]);

  if (err) return <p className="text-sm text-red-500" data-testid="crm-dash-error">{err}</p>;
  if (!s) return <div className="h-40 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-border border-t-foreground animate-spin" /></div>;
  const t = s.totals;
  const wl = [{ name: "Won", value: t.won }, { name: "Lost", value: t.lost }];

  const completeFu = async (id) => {
    try { await api.post(`/store-admin/crm/leads/${id}/followup/complete`); toast.success("Follow-up completed"); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const FuRow = ({ l }) => (
    <div className="flex items-center gap-3 py-2.5 border-b border-border last:border-0" data-testid={`dash-fu-${l.id}`}>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold truncate">{l.name}{l.company ? ` · ${l.company}` : ""}</p>
        <div className="flex flex-wrap items-center gap-2 mt-1"><StageBadge stage={l.stage} /><FuBadge lead={l} /></div>
      </div>
      <QuickActions name={l.name} phone={l.phone} whatsapp={l.whatsapp} email={l.email} />
      <button onClick={() => completeFu(l.id)} data-testid={`dash-fu-done-${l.id}`} title="Mark completed"
        className="h-8 w-8 rounded-lg border border-border flex items-center justify-center text-emerald-600 hover:bg-emerald-50 shrink-0">
        <CheckCircle2 className="h-4 w-4" />
      </button>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="crm-dashboard">
      <div className="grid grid-cols-2 sm:grid-cols-3 xl:grid-cols-5 gap-3">
        <StatCard label="Total Leads" value={t.leads} testid="stat-total-leads" />
        <StatCard label="New Leads" value={t.new} testid="stat-new-leads" />
        <StatCard label="Contacted" value={t.contacted} />
        <StatCard label="Interested" value={t.interested} />
        <StatCard label="Proposals Sent" value={t.proposals} />
        <StatCard label="Follow-ups Today" value={t.followups_today} tone="text-amber-600" testid="stat-fu-today" />
        <StatCard label="Overdue Follow-ups" value={t.followups_overdue} tone="text-red-500" testid="stat-fu-overdue" />
        <StatCard label="Won Deals" value={t.won} tone="text-emerald-600" />
        <StatCard label="Lost Deals" value={t.lost} tone="text-red-500" />
        <StatCard label="Conversion Rate" value={`${t.conversion}%`} testid="stat-conversion" />
        <StatCard label="Total Deal Value" value={fmtINR(t.deal_value)} testid="stat-deal-value" />
        <StatCard label="Won Value" value={fmtINR(t.won_value)} tone="text-emerald-600" />
        <StatCard label="Pending Pipeline" value={fmtINR(t.pending_value)} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-background p-5">
          <h3 className="font-display font-bold mb-4">Sales Pipeline</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={s.by_stage}><XAxis dataKey="stage" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} /><Tooltip /><Bar dataKey="count" fill={CHART} radius={[6, 6, 0, 0]} /></BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-2xl border border-border bg-background p-5">
          <h3 className="font-display font-bold mb-4">Won vs Lost</h3>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart><Pie data={wl} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} label>
                {wl.map((e, i) => <Cell key={e.name} fill={PIE[i]} />)}</Pie><Tooltip /><Legend /></PieChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-background p-5">
        <h3 className="font-display font-bold mb-4">Monthly Lead Growth</h3>
        <div className="h-52">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={s.monthly}><XAxis dataKey="month" tick={{ fontSize: 10 }} /><YAxis allowDecimals={false} tick={{ fontSize: 10 }} width={28} /><Tooltip /><Bar dataKey="leads" fill="#0ea5e9" radius={[6, 6, 0, 0]} /></BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <div className="rounded-2xl border border-border bg-background p-5" data-testid="dash-today-fu">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold">Today's Follow-ups</h3>
            <button onClick={() => goTab("followups")} className="text-xs font-bold text-muted-foreground hover:text-foreground">View all →</button>
          </div>
          {s.today_followups.length ? s.today_followups.map((l) => <FuRow key={l.id} l={l} />) : <p className="text-sm text-muted-foreground py-3">No follow-ups due today.</p>}
        </div>
        <div className="rounded-2xl border border-red-200 bg-red-50/40 p-5" data-testid="dash-overdue-fu">
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-display font-bold text-red-600">Overdue Follow-ups</h3>
            <button onClick={() => goTab("followups")} className="text-xs font-bold text-muted-foreground hover:text-foreground">View all →</button>
          </div>
          {s.overdue_followups.length ? s.overdue_followups.map((l) => <FuRow key={l.id} l={l} />) : <p className="text-sm text-muted-foreground py-3">Nothing overdue. Great job!</p>}
        </div>
      </div>

      <div className="rounded-2xl border border-border bg-background p-5">
        <h3 className="font-display font-bold mb-3">Recent Activity</h3>
        <Timeline activities={s.recent_activities} />
      </div>
    </div>
  );
}
